import appConfig from 'config/appConfig';
import {
  FilterParams,
  ICommunitiesListResponseBody,
  ICommunityDetailsResponseBody,
  ICommunityFavoriteRequestBody,
  ICommunitySubmitAuditDocumentsRequestBody,
  ICommunityUpdateRequestBody,
  IMessageResponse,
  IPaginationResponse,
  IProjectEvent,
  IProjectMilestone,
  IProjectsCount,
  IPublicationResponseBody,
  ITokenTickerExistsResponseBody,
} from 'types';
import { ICommunityStatement } from 'types/api/community-statement';
import API from '../API';

const COMMUNITY_API_PREFIX = `${appConfig.serviceUrl}/api/communities`;

const communityAPI = {
  getCommunities(params: FilterParams): Promise<IPaginationResponse<ICommunitiesListResponseBody>> {
    return API.get<IPaginationResponse<ICommunitiesListResponseBody>>(`${COMMUNITY_API_PREFIX}`, { params }).then(
      (response) => response.data,
    );
  },

  getAllCommunities(): Promise<ICommunityDetailsResponseBody[]> {
    return API.get<ICommunityDetailsResponseBody[]>(`${COMMUNITY_API_PREFIX}/all`).then((response) => response.data);
  },

  getCommunitiesCount(params: FilterParams): Promise<IProjectsCount> {
    return API.get<IProjectsCount>(`${COMMUNITY_API_PREFIX}/count`, { params }).then((response) => response.data);
  },

  getCommunityById(id: number): Promise<ICommunityDetailsResponseBody> {
    return API.get<ICommunityDetailsResponseBody>(`${COMMUNITY_API_PREFIX}/${id}`).then((response) => response.data);
  },

  getMyCommunity(): Promise<ICommunityDetailsResponseBody> {
    return API.get<ICommunityDetailsResponseBody>(`${COMMUNITY_API_PREFIX}/my`).then((response) => response.data);
  },

  updateMyCommunity(values: ICommunityUpdateRequestBody): Promise<Array<string>> {
    return API.patch<ICommunityUpdateRequestBody, Array<string>>(`${COMMUNITY_API_PREFIX}/my`, values).then(
      (response) => response.data,
    );
  },

  submitAuditDocuments(values: ICommunitySubmitAuditDocumentsRequestBody): Promise<Array<string>> {
    return API.patch<ICommunitySubmitAuditDocumentsRequestBody, Array<string>>(
      `${COMMUNITY_API_PREFIX}/my/audit-documents-submit`,
      values,
    ).then((response) => response.data);
  },

  submitMyCommunity(): Promise<void> {
    return API.put<void, void>(`${COMMUNITY_API_PREFIX}/my/submit`).then((response) => response.data);
  },

  rollbackMyCommunity(): Promise<void> {
    return API.patch<void, void>(`${COMMUNITY_API_PREFIX}/my/rollback`).then((response) => response.data);
  },

  markForDeletion(id: number, body: { markedForDeletion: boolean }): Promise<void> {
    return API.patch<{ markedForDeletion: boolean }, void>(
      `${COMMUNITY_API_PREFIX}/${id}/mark-for-deletion`,
      body,
    ).then((response) => response.data);
  },

  //STATEMENTS

  getCommunityStatements(communityId: number): Promise<IPaginationResponse<ICommunityStatement>> {
    return API.get<IPaginationResponse<ICommunityStatement>>(`${COMMUNITY_API_PREFIX}/${communityId}/statements`).then(
      (response) => response.data,
    );
  },

  createCommunityStatement(projectId: number, data: ICommunityStatement): Promise<ICommunityStatement> {
    return API.post<Omit<ICommunityStatement, 'id'>, ICommunityStatement>(
      `${COMMUNITY_API_PREFIX}/${projectId}/statements`,
      data,
    ).then((response) => response.data);
  },

  editCommunityStatement(
    projectId: number,
    data: Omit<ICommunityStatement, 'id'>,
    statementId: number,
  ): Promise<ICommunityStatement> {
    return API.patch<Omit<ICommunityStatement, 'id'>, ICommunityStatement>(
      `${COMMUNITY_API_PREFIX}/${projectId}/statements/${statementId}`,
      data,
    ).then((response) => response.data);
  },

  deleteCommunityStatement(projectId: number, statementId: number): Promise<ICommunityStatement> {
    return API.delete<ICommunityStatement>(`${COMMUNITY_API_PREFIX}/${projectId}/statements/${statementId}`).then(
      (response) => response.data,
    );
  },

  //PUBLICATIONS
  getCommunityPublications({
    projectId,
    limit,
  }: {
    projectId: number;
    limit: number;
  }): Promise<IPaginationResponse<IPublicationResponseBody>> {
    return API.get<IPaginationResponse<IPublicationResponseBody>>(
      `${COMMUNITY_API_PREFIX}/${projectId}/publications?limit=${limit}`,
    ).then((response) => response.data);
  },

  createCommunityPublication(projectId: number, data: IPublicationResponseBody): Promise<IPublicationResponseBody> {
    return API.post<Omit<IPublicationResponseBody, 'id'>, IPublicationResponseBody>(
      `${COMMUNITY_API_PREFIX}/${projectId}/publications`,
      data,
    ).then((response) => response.data);
  },

  editCommunityPublication(
    projectId: number,
    data: IPublicationResponseBody,
    publicationId: number,
  ): Promise<IPublicationResponseBody> {
    return API.patch<Omit<IPublicationResponseBody, 'id'>, IPublicationResponseBody>(
      `${COMMUNITY_API_PREFIX}/${projectId}/publications/${publicationId}`,
      data,
    ).then((response) => response.data);
  },

  deleteCommunityPublication(projectId: number, publicationtId: number): Promise<IPublicationResponseBody> {
    return API.delete<IPublicationResponseBody>(
      `${COMMUNITY_API_PREFIX}/${projectId}/publications/${publicationtId}`,
    ).then((response) => response.data);
  },

  //MILESTONES
  getCommunityMilestones({
    projectId,
    limit,
  }: {
    projectId: number;
    limit: number;
  }): Promise<IPaginationResponse<Omit<IProjectMilestone, 'id'>>> {
    return API.get<IPaginationResponse<Omit<IProjectMilestone, 'id'>>>(
      `${COMMUNITY_API_PREFIX}/${projectId}/milestones?limit=${limit}`,
    ).then((response) => response.data);
  },

  createCommunitytMilestone(projectId: number, data: Omit<IProjectMilestone, 'id'>): Promise<IProjectMilestone> {
    return API.post<Omit<IProjectMilestone, 'id'>, IProjectMilestone>(
      `${COMMUNITY_API_PREFIX}/${projectId}/milestones`,
      data,
    ).then((response) => response.data);
  },

  editCommunityMilestone(
    projectId: number,
    data: Omit<IProjectMilestone, 'id'>,
    milestoneId: number,
  ): Promise<IProjectMilestone> {
    return API.patch<Omit<IProjectMilestone, 'id'>, IProjectMilestone>(
      `${COMMUNITY_API_PREFIX}/${projectId}/milestones/${milestoneId}`,
      data,
    ).then((response) => response.data);
  },

  deleteCommunityMilestone(projectId: number, milestoneId: number): Promise<IProjectMilestone> {
    return API.delete<IProjectMilestone>(`${COMMUNITY_API_PREFIX}/${projectId}/milestones/${milestoneId}`).then(
      (response) => response.data,
    );
  },

  //EVENTS
  getCommunityEvents({
    projectId,
    limit,
  }: {
    projectId: number;
    limit: number;
  }): Promise<IPaginationResponse<Omit<IProjectEvent, 'id'>>> {
    return API.get<IPaginationResponse<Omit<IProjectEvent, 'id'>>>(
      `${COMMUNITY_API_PREFIX}/${projectId}/events?limit=${limit}`,
    ).then((response) => response.data);
  },

  createCommunityEvent(projectId: number, data: Omit<IProjectEvent, 'id'>): Promise<IProjectEvent> {
    return API.post<Omit<IProjectEvent, 'id'>, IProjectEvent>(`${COMMUNITY_API_PREFIX}/${projectId}/events`, data).then(
      (response) => response.data,
    );
  },

  editCommunityEvent(projectId: number, data: Omit<IProjectEvent, 'id'>, eventId: number): Promise<IProjectEvent> {
    return API.patch<Omit<IProjectEvent, 'id'>, IProjectEvent>(
      `${COMMUNITY_API_PREFIX}/${projectId}/events/${eventId}`,
      data,
    ).then((response) => response.data);
  },

  deleteCommunityEvent(projectId: number, eventId: number): Promise<IProjectEvent> {
    return API.delete<IProjectEvent>(`${COMMUNITY_API_PREFIX}/${projectId}/events/${eventId}`).then(
      (response) => response.data,
    );
  },

  // FAVORITES
  addToFavorites(id: number): Promise<IMessageResponse> {
    return API.post<ICommunityFavoriteRequestBody, IMessageResponse>(`${COMMUNITY_API_PREFIX}/favorites`, {
      projectId: id,
    }).then((response) => response.data);
  },

  removeFromFavorites(id: number): Promise<IMessageResponse> {
    return API.delete<IMessageResponse>(`${COMMUNITY_API_PREFIX}/favorites/${id}`).then((response) => response.data);
  },

  getFavoriteCommunities(params: FilterParams): Promise<IPaginationResponse<ICommunitiesListResponseBody>> {
    return API.get<IPaginationResponse<ICommunitiesListResponseBody>>(`${COMMUNITY_API_PREFIX}/favorites`, {
      params,
    }).then((response) => response.data);
  },

  checkTicker(tokenTicker: string): Promise<ITokenTickerExistsResponseBody> {
    return API.post<{ tokenTicker: string }, ITokenTickerExistsResponseBody>(`${COMMUNITY_API_PREFIX}/check-ticker`, {
      tokenTicker,
    }).then((response) => response.data);
  },
};

export default communityAPI;
